import { useEffect } from "react";
import { socket } from "src/App";
import { useSnackbar } from "src/components/snackbar";
import { useAuthContext } from "src/auth/useAuthContext";

function sameVenue(user, venueId) {
    return user?.venueId === venueId?.id || user?.venueId === venueId;
}

function sameLevel(user, levelId, isSuperAdmin) {
    return (
        user?.levelId === levelId ||
        user?.levelId === levelId?.id ||
        user?.role === "admin" ||
        isSuperAdmin
    );
}

function formatOrderType(type) {
    if (!type) return "";
    return type
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/Qr/g, "QR")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function GlobalSocketListeners() {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthContext();

    useEffect(() => {
        if (!user) return;

        const handleOrderDrafted = (payload) => {
            const action = payload?.action || "add";
            const data = payload?.order || payload;

            if (!sameVenue(user, data?.venueId)) return;
            if (!sameLevel(user, data?.levelId, data?.isSuperAdmin)) return;
            if (!user?.permissions?.canTakePaymentOnPOS) return;

            const staffName =
                `${data?.assignee?.firstName || ""} ${data?.assignee?.lastName || ""}`.trim();

            if (action === "add") {
                enqueueSnackbar(
                    `New ${formatOrderType(data?.orderType)} Order added by ${staffName || "Staff"} (Bill # ${data?.billNumber})`,
                    {
                        variant: "info",
                        autoHideDuration: 4000,
                        key: `order-add-${data?.id}-${Date.now()}`,
                    }
                );
            }

            if (action === "update") {
                enqueueSnackbar(
                    `Order Updated (Bill # ${data?.billNumber})`,
                    {
                        variant: "warning",
                        autoHideDuration: 4000,
                        key: `order-update-${data?.id}-${Date.now()}`,
                    }
                );
            }
        };

        const handleCompletedOrdersForPOS = (data) => {
            const staffName =
                `${data?.assignee?.firstName || ""} ${data?.assignee?.lastName || ""}`.trim();

            if (!sameVenue(user, data?.venueId)) return;
            if (!sameLevel(user, data?.levelId, data?.isSuperAdmin)) return;

            enqueueSnackbar(
                `Order prepared from kitchen (Bill # ${data?.billNumber})`,
                {
                    variant: "success",
                    autoHideDuration: 4000,
                    key: `kitchen-prepared-${data?.id}-${Date.now()}`,
                }
            );
        };

        const handlePurchaseOrderChanged = (data) => {
            if (!sameVenue(user, data?.venueId)) return;
            if (!sameLevel(user, data?.levelId)) return;
            enqueueSnackbar("Purchase Order list updated", {
                variant: "info",
                autoHideDuration: 4000,
                key: `purchase-order-${Date.now()}`,
            });
        };
        const handleOrderStateUpdated = (data) => {
            if (!sameVenue(user, data?.data?.venueId)) return;
            if (!sameLevel(user, data?.data?.levelId)) return;
            if (!user?.permissions?.canTakePaymentOnPOS) return;
            if (data?.state === "completed" || data?.state === "delivered") {
                enqueueSnackbar('Order served', {
                    variant: "success",
                    autoHideDuration: 4000,
                    key: `order-completed-${data?.orderId}-${Date.now()}`,
                });
            }
        };

        const handleCancelCompletedOrders = (data) => {
            if (!sameVenue(user, data?.venueId)) return;
            if (!sameLevel(user, data?.levelId, data?.isSuperAdmin)) return;
            if (!user?.permissions?.canTakePaymentOnPOS) return;
            if (data?.state === "noShow") {
                enqueueSnackbar(
                    'Cancelled completed order',
                    {
                        variant: "error",
                        autoHideDuration: 4000,
                        key: `kitchen-canceled-${data?.id}-${Date.now()}`,
                    }
                );
                return;
            }
        }
        const handleIncomingOrdersForPos = (data) => {
            if (!sameVenue(user, data?.venueId)) return;
            if (!sameLevel(user, data?.levelId, data?.isSuperAdmin)) return;
            if (!user?.permissions?.canTakePaymentOnPOS) return;

            enqueueSnackbar(
                `New incoming order received (Bill # ${data?.billNumber})`,
                {
                    variant: "info",
                    autoHideDuration: 4000,
                    key: `incoming-order-${data?.id}-${Date.now()}`,
                }
            );
        };

        socket.on("OrderDrafted", handleOrderDrafted);
        socket.on("completedOrdersForPOS", handleCompletedOrdersForPOS);
        socket.on("purchaseOrderChanged", handlePurchaseOrderChanged);
        socket.on("OrderStateUpdated", handleOrderStateUpdated);
        socket.on("completedOrderRemove", handleCancelCompletedOrders);
        socket.on("IncomingOrdersForPos", handleIncomingOrdersForPos);

        return () => {
            socket.off("OrderDrafted", handleOrderDrafted);
            socket.off("completedOrdersForPOS", handleCompletedOrdersForPOS);
            socket.off("purchaseOrderChanged", handlePurchaseOrderChanged);
            socket.off("OrderStateUpdated", handleOrderStateUpdated);
            socket.off("completedOrderRemove", handleCancelCompletedOrders);
            socket.off("IncomingOrdersForPos", handleIncomingOrdersForPos);
        };
    }, [user, enqueueSnackbar]);

    return null;
}